import { requestQuoteRevisionInputSchema } from '@repo/api-contracts';
import { useState } from 'react';
import { useInvalidateMyQuotes } from '@/entities/quote';
import { trpc } from '@/shared/api';
import { getErrorMessage, getTrpcErrorCode } from '@/shared/lib';

export interface RequestRevisionInput {
  readonly quoteRequestId: string;
  /** 화면에 보이는 최신 발행본 id — 서버가 최신이 아니면 CONFLICT 로 거절한다. */
  readonly versionId: string;
  readonly reason: string;
}

export interface UseRequestRevisionResult {
  /** 성공하면 true. 검증 실패·서버 거절·버전 충돌이면 false. */
  readonly submit: (input: RequestRevisionInput) => Promise<boolean>;
  readonly isPending: boolean;
  readonly fieldError: string | null;
  readonly formError: string | null;
  /** 보낸 발행본이 최신이 아니어서(CONFLICT) 상세를 다시 불러온 상태 — 호출부는 입력을 유지한다. */
  readonly isOutdated: boolean;
}

const REASON_MESSAGE = '수정 요청 사유를 1~1,000자로 입력해 주세요.';
const FORM_ERROR_FALLBACK = '수정 요청에 실패했습니다.';

/**
 * 수정 요청(quotes.requestRevision, 견적 설계 §7-6).
 *
 * CONFLICT(발행본이 바뀜·상태 변경·만료)는 폼 오류가 아니라 "최신 상세 다시 불러오기"다 —
 * 사유는 폼이 그대로 들고 있고, 상세 invalidate 로 새 발행본이 오면 같은 사유로 다시 보낼 수 있다.
 */
export const useRequestRevision = (): UseRequestRevisionResult => {
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [isOutdated, setIsOutdated] = useState(false);
  const invalidate = useInvalidateMyQuotes();
  const mutation = trpc.quotes.requestRevision.useMutation();

  const submit = async (input: RequestRevisionInput): Promise<boolean> => {
    setFieldError(null);
    setFormError(null);
    setIsOutdated(false);

    const parsed = requestQuoteRevisionInputSchema.safeParse(input);

    if (!parsed.success) {
      setFieldError(REASON_MESSAGE);
      return false;
    }

    try {
      await mutation.mutateAsync(parsed.data);
      await Promise.all([invalidate.detail(input.quoteRequestId), invalidate.list()]);
      return true;
    } catch (error) {
      if (getTrpcErrorCode(error) === 'CONFLICT') {
        setIsOutdated(true);
        // 상세뿐 아니라 목록의 상태 열도 낡았을 수 있다.
        await Promise.all([invalidate.detail(input.quoteRequestId), invalidate.list()]);
      } else {
        setFormError(getErrorMessage(error, FORM_ERROR_FALLBACK));
      }

      return false;
    }
  };

  return { submit, isPending: mutation.isPending, fieldError, formError, isOutdated };
};
